import React, { useState, useEffect, useContext, useCallback, useRef, useMemo } from 'react';
import { makeStyles, useTheme } from '@mui/styles';
import PropTypes from 'prop-types';
import Checkbox from '@mui/material/Checkbox';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Chip from '@mui/material/Chip';
import Grid from '@mui/material/Grid';
import clsx from 'clsx';

import Box from '@mui/material/Box';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { autocompleteClasses, Typography } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import TransformIcon from '@mui/icons-material/Transform';
import DataContext from '../../data/data-context';
import classnames, { DragIndicatorSVG, HexEscape, NormalNode } from './utils';
import PopupMenu from '../popupmenu';
import Validator from '../validator';
import { MitreColors } from '../mitre';
import SensorSelector from './sensor-selector';
import AssetSelector from './asset-selector';
import PCRWidget from './pcr-widget';
import BTJobSelector from './bt-job-selector';
import Tooltip from '../styled/tooltip';


const MC2CHECK = process.env.BRICATA_APPLIANCE_TYPE === 'mc2';

const useStyles = makeStyles((theme) => ({
    rowWrapper: {
        padding: '0px 10px 0px 0',
        height: '100%',
        marginTop:'10px',
        boxSizing: 'border-box',
        marginLeft: '30px',
    },
    expNode: {
        boxShadow: 'none',
        padding: '0',
        display: 'flex',
        alignItems: 'center',
        borderRadius:'2px',
        backgroundColor: theme.palette.background.varBackground,
        '& .rowContents': {
            boxShadow: 'none',
            backgroundColor: theme.styled.filterbuilder.varBackground,
            color: theme.palette.text.primary,
            borderStyle: 'none',
            padding:'2px 0 2px 0',
            display: 'flex',
            alignItems:'center',
            borderRadius:'2px',
        },
        '& .rowLabel': {
            padding: 0,
        },
        '& .moveHandle': {
            background: `${theme.styled.filterbuilder.varBackground} url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' style='width:24px;height:24px' viewBox='0 0 24 24'%3E%3Cpath fill='${HexEscape(theme.styled.filterbuilder.varHandle)}' stroke='transparent' d='${DragIndicatorSVG}' /%3E%3C/svg%3E") no-repeat center;`,
            width: 30,
            borderRightStyle: 'none',
            marginRight: -5,
            borderStyle: 'none',
            boxShadow: 'none',
        },
        '& .loadingHandle': {
            background: `${theme.styled.filterbuilder.varBackground} url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' style='width:24px;height:24px' viewBox='0 0 24 24'%3E%3Cpath fill='${HexEscape(theme.styled.filterbuilder.varHandle)}' stroke='transparent' d='${DragIndicatorSVG}' /%3E%3C/svg%3E") no-repeat center;`,
            width: 30,
            borderRightStyle: 'none',
            marginRight: -5,
            borderStyle: 'none',
        },
        '& .iconButton': {
            color: 'black',
        },
    },
    variable: {
        ...theme.typography.button,
        backgroundColor: theme.palette.background.default,
        color: theme.palette.text.primary,
        borderStyle: 'none',
        borderRadius: 4,
        display: 'inline',
        fontSize: 'small',
        width: 150,
        textAlign: 'right',
        padding: '5px 10px',
    },
    value: {
        minWidth: 200,
        paddingLeft: theme.spacing(1),
        flex: 1,
        fontSize: 14,
        color: theme.styled.filterbuilder.varValue,

        '& input': {
            color: theme.styled.filterbuilder.varValue,
            fontSize: 14,
            '&::placeholder': {
                textOverflow: 'ellipsis !important',
            },
        },
        '& .MuiSelect-select.MuiSelect-select': {
            color: theme.styled.filterbuilder.varValue,
        },
    },
    valueColor: {
        color: theme.styled.filterbuilder.varValue,
    },
    operator: {
        // color: theme.styled.filterbuilder.varAction,
        ...theme.typography.button,
        backgroundColor: theme.styled.filterbuilder.opButton,
        borderStyle: 'none',
        borderRadius: 4,
        display: 'inline',
        marginTop: 2,
        marginBottom: 2,
        marginLeft: 8,
        marginRight: 5,
        fontSize: 'small',
        padding: '2px 5px',
        width: 50,
        maxWidth: 50,
        minWidth: 50,
    },
    popperOverride: {
        width: 'auto',
        display: 'inline',
    },
    info: {
        paddingLeft: 20,
        fontSize: 'smaller',
        paddingTop: 2,
        opacity: 0.6,
    },
    row: {
        height: '100%',
        whiteSpace: 'nowrap',
        display: 'flex',
    },
    'rowLandingPad::before': {
        backgroundColor: 'lightblue',
        border: '3px dashed white',
        content: '',
        position: 'absolute',
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
        zIndex: -1,
    },
    '.rowCancelPad::before': {
        backgroundColor: '#e6a8ad'
    },
    rowSearchMatch: {
        outline: 'solid 3px #0080ff'
    },
    rowSearchFocus: {
        outline: 'solid 3px #fc6421'
    },

    // rst__rowContents
    // rst__rowLabel
    // rst__rowToolbar
    // rst__moveHandle
    // rst__toolbarButton
    align: {
        display: 'inline-block',
        verticalAlign: 'middle'
    },
    rowContents: {
        position: 'relative',
        height: '100%',
        border: 'solid #bbb 1px',
        borderLeft: 'none',
        boxShadow: '0 2px 2px -2px',
        padding: '0 5px 0 10px',
        borderRadius: '2px',
        minWidth: '230px',
        flex: '1 0 auto',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        backgroundColor: 'white',
    },
    '.rtl.rowContents': {
        borderRight: 'none',
        borderLeft: 'solid #bbb 1px',
        padding: '0 10px 0 5px',
    },
    rowContentsDragDisabled: {
        borderLeft: 'solid #bbb 1px',
    },
    '.rtl.rowContentsDragDisabled': {
        borderRight: 'solid #bbb 1px',
        borderLeft: 'solid #bbb 1px',
    },
    rowLabel: {
        flex: '0 1 auto',
        paddingRight: '20px',
    },
    '.rtl.rowLabel': {
        paddingLeft: '20px',
        paddingRight: 'inherit',
    },
    rowToolbar: {
        flex: '0 1 auto',
        display: 'flex',
    },
    rowTitle: {
        fontWeight: 'bold',
    },
    rowTitleWithSubtitle: {
        fontSize: '85%',
        display: 'block',
        height: '0.8rem',
    },
    rowSubtitle: {
        fontSize: '70%',
        lineHeight: '1',
    }
}));

const hints = {
    ip: '12.34.5.6 or 12.34.5.0/24 or 10.0.0.0 - 10.0.0.255',
    port: '80, 8080 - 8088',
    int: 'Enter a number',
    str: 'Exact search term',
    txt: 'Partial search term',
    bool: 'True or False'
};
const valueTypes = {
    ip: 'ipRange',
    port: 'portRange',
    pcr: 'pcr',
};

const valueProps = {
};

const component = {
    sensor_uuid: SensorSelector,
    pcr: PCRWidget,
    ip: AssetSelector,
    bt_job_uuid: MC2CHECK ? undefined : BTJobSelector,
};

const NodeExpression = (props) => {
    const {
        node,
        path,
        level,
        fields,
        onDelete,
        onChange,
        mappings,
        isDragging,
        didDrop,
        rowDirection = 'ltr',
        style = {},
        className = '',
        isSearchMatch = false,
        isSearchFocus = false,
        isOver,
        draggedNode = null,
        onValidate,
        canDrop,
        canDrag = false,
        ...otherProps
    } = props
    const classes = useStyles();
    const theme = useTheme();
    const [valueSelect, setValueSelect] = useState({
        bool: ['true', 'false'],
        mitre: [],
    });
    const operators = [
        { key: '=', title: '=' },
        { key: 'exists', title: 'exists' },
    ];

    const { mitreCategories } = useContext(DataContext);
    const [info, setInfo] = useState({});
    const [valid, setValid] = useState({ kicker: 0 });
    const validatorRef = useRef(null);
    const [value, setValue] = useState(node.value || '');
    const rowDirectionClass = rowDirection === 'rtl' ? 'rtl' : null;

    const getMappedInfo = v => {
        if (!mappings[v.variable] ||
            (mappings[v.variable].length === 1 &&
                mappings[v.variable][0] === v.variable)) return undefined;

        const ms = mappings[v.variable].map(m => `${info[m].title}`);
        return (<Tooltip
            arrow placement="top"
            size="small"
            title={ms.join(' OR ')}>
            <TransformIcon color='primary' className={classes.mapped} size='small' />
        </Tooltip>);
    };

    useEffect(() => {
        if (!mitreCategories) return;
        setValueSelect(current => ({
            ...current,
            mitre: mitreCategories.map(c => ({
                key: c.id,
                value: c.id,
                title: c.id,
                info: c.description,
                icon: <Chip
                    label={c.name}
                    className={classes.chip}
                    size="small"
                    style={{
                        backgroundColor: MitreColors[c.id],
                        color: theme.palette.getContrastText(MitreColors[c.id])
                    }} />,
            })),
        }));
    }, [mitreCategories, classes, theme]);

    useEffect(() => {
        const infomap = fields.reduce((a, c) => {
            return {
                ...a,
                [c.variable]: {
                    ...c,
                    title: c.title,
                    hint: c.hint ? c.hint : hints[c.type || 'str'],
                    hint2: c.hint2,
                    valueType: c.valueType || valueTypes[c.type],
                    props: c.props || valueProps[c.type],
                    select: (c.select || valueSelect[c.type]) !== undefined,
                    options: c.select || valueSelect[c.type] || [],
                    component: component[c.type],
                },
            };
        }, {});
        setInfo(infomap);
    }, [fields, valueSelect]);

    const handleOnDisable = (e) => {
        setValid(current => ({ ...current, kicker: current.kicker + 1 }));
        onChange(node.id, { disabled: !node.disabled }, valid.validID,
            valid.valid || !e.target.checked);
    };

    const handleVariableChange = variable => {
        setValid(current => ({ ...current, kicker: current.kicker + 1 }));
        onChange(node.id, { ...NormalNode(variable), value:'' }, valid.validID, valid.valid);
        setValue('')
    };

    const handleOperatorChange = (opObj) => {
        setValid((prev) => ({ ...prev, kicker: prev.kicker + 1 }));
        onChange(node.id, { disabled: node.disabled, operator: opObj.key || '=' },);
    };

    const handleOnChange = useCallback((e, validID, valid, validated, value) => {
        // update value only if it changed
        if (value !== undefined) {
            setValue(prev => (prev !== value ? value : prev));
        }
        // update validation only if something actually changed
        setValid(current => {
            const sameValidation = current.validID === validID && current.valid === valid;
            return sameValidation
                ? current
                : { ...current, validID, valid, kicker: current.kicker + 1};
        });
        // // we only need to call onChange for outgoing values
        if (validated) {
            onValidate(validID, valid || !!node.disabled);
        } else {
            onChange(node.id, { value }, validID, valid || !!node.disabled);
        }
    }, [onValidate, onChange, node.id, node.disabled]);

    useEffect(() => {
        setValue(node.value || '');
    }, [node.value]);

    const isDraggedDescendant = draggedNode && false;
    const isLandingPadActive = !didDrop && isDragging;


    return (
        <Box style={{ height: '100%' }} {...otherProps}>
            <Box className={classnames('rowWrapper', rowDirectionClass, classes.rowWrapper)}>
                <Box
                    className={classnames(
                        'row',
                        isLandingPadActive && 'rowLandingPad',
                        isLandingPadActive && !canDrop && 'rowCancelPad',
                        isSearchMatch && 'rowSearchMatch',
                        isSearchFocus && 'rowSearchFocus',
                        rowDirectionClass,
                        classes.expNode,
                        className
                    )}
                    style={{ opacity: isDraggedDescendant ? 0.5 : 1, ...style }}>
                    <Box className={classnames(
                        'rowContents',
                        !canDrag && 'rowContentsDragDisabled',
                        rowDirectionClass
                    )} >
                        <DragIndicatorIcon fontSize="small" color="action" sx={{ ml: 1, mr: 1, cursor: 'grab', color:theme.styled.filterbuilder.varHandle }}/>
                        <Box className={classnames('rowToolbar', rowDirectionClass)}>
                            <PopupMenu
                                tooltip={(info[node.variable] || {}).hint}
                                className={classes.variable}
                                popperclass={classes.popperOverride}
                                items={fields}
                                getInfo={getMappedInfo}
                                getInfo2={() => (info[node.variable] || {}).hint2}
                                selection={(info[node.variable] || {}).title}
                                itemkey='variable'
                                itemTitle='title'
                                onSelect={handleVariableChange}
                                title={(info[node.variable] || {}).title}
                                size='small'
                                placement={'bottom-start'}
                                zIndex={1500} />
                        </Box>

                        <PopupMenu key={'operator'}
                            className={classes.operator}
                            popperclass={classes.popperOverride}
                            items={operators}
                            itemkey='key'
                            itemTitle='title'
                            title={node.operator || '='}
                            onSelect={handleOperatorChange}
                            variant='outlined'
                            size='small'
                            placement={'bottom-end'}
                            zIndex={1500} />


                        <Box className={classnames('rowLabel', rowDirectionClass)}>
                            <Validator
                                variant="standard"
                                className={classes.value}
                                style={{ display: node.operator === 'exists' ? 'none' : '' }}
                                placeholder={(info[node.variable] || {}).hint}
                                allowEmpty={node.operator === 'exists'}
                                disabled={node.operator === 'exists'}
                                autoCorrect="off"
                                autoComplete="off"
                                ref={validatorRef}
                                value={value}
                                valueType={(info[node.variable] || {}).valueType}
                                type={(info[node.variable] || {}).type}
                                inputProps={{
                                    'aria-label': (info[node.variable] || {}).hint,
                                    spellCheck: false,
                                    ...(info[node.variable] || {}).props
                                }}
                                select={(info[node.variable] || {}).select}
                                onChange={handleOnChange}
                                kick={valid.kicker}
                                component={(info[node.variable] || {}).component}
                                componentclass={classes.valueColor}>
                                {((info[node.variable] || {}).options || []).map(option => {
                                    // can pass objects with { key, value, title, icon } for fancy lists
                                    if (typeof option === 'object') {
                                        return <MenuItem key={option.key} value={option.value}>
                                            <Grid container
                                                direction="row"
                                                alignItems="center"
                                                justifyContent="space-between"
                                                wrap='nowrap'>
                                                <Grid>{option.icon}</Grid>
                                                <Grid container
                                                    justifyContent="flex-end"
                                                    alignItems="center">
                                                    <Grid className={classes.info}>
                                                        {option.title}
                                                    </Grid>
                                                </Grid>
                                            </Grid>
                                        </MenuItem>;
                                    }
                                    return <MenuItem key={option} value={option}>
                                        {option}
                                    </MenuItem>;
                                })}
                            </Validator>
                        </Box>

                        <Box className={classes.rowToolbar}>
                            <Checkbox
                                title={node.disabled ? 'enable filter' : 'disable filter'}
                                aria-label={node.disabled ? 'enable filter' : 'disable filter'}
                                checked={!node.disabled}
                                color="default"
                                size='small'
                                className={classes.action}
                                onChange={handleOnDisable}
                                inputProps={{ 'aria-label': 'enabled' }}
                            />
                            <IconButton key={'deletenode'}
                                title="delete filter"
                                variant='outlined'
                                aria-label='delete filter'
                                onClick={() => onDelete(node.id,valid.validID)}
                                className={classes.action}
                                size='small'>
                                <DeleteIcon variant="primary" />
                            </IconButton>
                        </Box>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

NodeExpression.propTypes = {
    node: PropTypes.shape({
        id: PropTypes.string.isRequired,
        value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool]),
        operator: PropTypes.string,
        variable: PropTypes.string,
        disabled: PropTypes.bool,
        children: PropTypes.array,
        expanded: PropTypes.bool,
        type: PropTypes.string,
    }).isRequired,
    fields: PropTypes.array.isRequired,
    mappings: PropTypes.object,
    onDelete: PropTypes.func.isRequired,
    onChange: PropTypes.func.isRequired,
    onValidate: PropTypes.func.isRequired,
    rowDirection: PropTypes.oneOf(['ltr', 'rtl']),
    className: PropTypes.string,
    isOver: PropTypes.bool,
};
export default NodeExpression;